import { useState, useCallback } from "react";
import { Layout, Typography, Avatar, Space, Button, Tooltip, message } from "antd";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { motion, AnimatePresence } from "framer-motion";
import SidebarMenu from "../Components/SidebarMenu";
import { Outlet, useLocation } from "react-router-dom";
import { ReloadOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { useAppSelector, useAppDispatch } from "store/hooks";
import { RootState } from "store";
import { baseApi } from "store/api/baseApi";
import { useRefreshSessionMutation } from "store/api/authApi";
import { logout } from "store/authSlice";
import { useSessionExpiry } from "../hooks/useSessionExpiry";
import { colors, shadows } from "../styles/theme";
import OSGLogo from "../OSG_Logo.png";

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const LayoutPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const username = useAppSelector(
    (state: RootState) => state.auth.auth.username
  );
  const roleName = useAppSelector(
    (state: RootState) => state.auth.auth.roleName
  );

  const handleSessionEnd = useCallback(() => {
    sessionStorage.clear();
    localStorage.clear();
    dispatch(logout());
    dispatch(baseApi.util.resetApiState());
    message.error("Your session has expired. Please log in again.");
    window.location.href = "/";
  }, [dispatch]);

  const {
    secondsRemaining,
    isWarningActive,
    resetIdle,
  } = useSessionExpiry(handleSessionEnd, true);

  const [refreshSession, { isLoading: refreshLoading }] = useRefreshSessionMutation();

  const handleExtendSession = useCallback(async () => {
    const refreshToken = sessionStorage.getItem("refreshToken");
    if (!refreshToken) {
      message.error("Cannot extend session. Please save your work and log in again.");
      return;
    }
    try {
      const result = await refreshSession({ refreshToken }).unwrap();
      if (result.accessToken) {
        sessionStorage.setItem("authToken", result.accessToken);
        const expiryAt = Date.now() + (result.expiresIn ?? 45 * 60) * 1000;
        sessionStorage.setItem("authTokenExpiry", String(expiryAt));
      }
      if (result.refreshToken) {
        sessionStorage.setItem("refreshToken", result.refreshToken);
      }
      resetIdle();
      message.success("Session extended successfully.");
    } catch {
      message.error(
        "Could not extend session. Please save your work and log in again."
      );
    }
  }, [refreshSession, resetIdle]);

  const formatCountdown = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Sidebar - Dark with Brand Accents */}
      <Sider
        trigger={null}
        collapsible={true}
        collapsed={collapsed}
        width={260}
        collapsedWidth={72}
        className="osg-sidebar"
        style={{
          background: colors.sidebarBg,
          boxShadow: "4px 0 20px rgba(0, 0, 0, 0.15)",
          position: "fixed",
          height: "100vh",
          left: 0,
          top: 0,
          zIndex: 100,
        }}
      >
        {/* Logo Section */}
        <motion.div
          className="osg-sidebar-logo"
          style={{
            height: collapsed ? 72 : 192,
            margin: collapsed ? "16px 8px" : "32px 32px",
            padding: collapsed ? "8px" : "24px 32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          initial={false}
          animate={{
            height: collapsed ? 48 : 112,
          }}
          transition={{ duration: 0.2 }}
        >
          <motion.img
            src={OSGLogo}
            alt="OSG Logo"
            style={{
              maxHeight: collapsed ? 64 : 160,
              maxWidth: collapsed ? 36 : "100%",
              width: "auto",
              objectFit: "contain",
            }}
            initial={false}
            animate={{
              maxHeight: collapsed ? 40 : 96,
              maxWidth: collapsed ? 40 : "100%",
            }}
            transition={{ duration: 0.2 }}
          />
        </motion.div>

        {/* Divider */}
        <div className="osg-sidebar-divider" />

        {/* Menu */}
        <SidebarMenu collapsed={collapsed} />
      </Sider>

      <Layout
        style={{
          marginLeft: collapsed ? 72 : 260,
          transition: "margin-left 0.2s",
        }}
      >
        {/* Navbar - Sticky, with optional session warning inline */}
        <Header
          className="osg-navbar"
          style={{
            padding: "0 24px",
            background: colors.white,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: shadows.sm,
            borderBottom: `1px solid ${colors.gray200}`,
            height: 64,
            position: "sticky",
            top: 0,
            zIndex: 99,
          }}
        >
          {/* LEFT - Toggle Button + Title */}
          <Space size="middle" align="center">
            <Tooltip title={collapsed ? "Expand menu" : "Collapse menu"}>
              <Button
                type="text"
                icon={
                  collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
                }
                onClick={() => setCollapsed(!collapsed)}
                className="osg-navbar-toggle"
                style={{
                  fontSize: 16,
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  color: colors.accent,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            </Tooltip>
            <div>
              <Text
                strong
                style={{
                  fontSize: 16,
                  color: colors.gray800,
                  letterSpacing: "-0.01em",
                }}
              >
                Order Tracking System
              </Text>
            </div>
          </Space>

          {/* CENTER/RIGHT - Session warning (when active) + Profile */}
          <Space size="middle" align="center" style={{ height: 64 }}>
            {isWarningActive && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  height: 36,
                  padding: "0 10px",
                  background: "#fffbeb",
                  border: `1px solid ${colors.warning}`,
                  borderRadius: 6,
                  flexShrink: 0,
                }}
              >
                <ClockCircleOutlined style={{ color: colors.warning, fontSize: 14 }} />
                <span style={{ fontSize: 12, color: colors.gray800, lineHeight: 1, whiteSpace: "nowrap" }}>
                  Expires in <strong style={{ color: colors.accentDark }}>{formatCountdown(secondsRemaining)}</strong>
                </span>
                <Button
                  type="primary"
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={handleExtendSession}
                  loading={refreshLoading}
                  style={{ height: 28, paddingLeft: 8, paddingRight: 8, fontSize: 12, lineHeight: 1 }}
                >
                  Extend
                </Button>
              </div>
            )}
            <Space size={12} align="center">
              <div style={{ textAlign: "right" }}>
                <Text
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: colors.gray700,
                    display: "block",
                    lineHeight: 1.3,
                  }}
                >
                  {username}
                </Text>
                <Text
                  style={{
                    fontSize: 12,
                    color: colors.gray400,
                    display: "block",
                    lineHeight: 1.3,
                  }}
                >
                  {roleName ?? "—"}
                </Text>
              </div>
              <Avatar
                className="osg-avatar"
                size={40}
                style={{
                  background: colors.primary,
                  fontWeight: 500,
                  fontSize: 16,
                }}
              >
                {username.charAt(0).toUpperCase()}
              </Avatar>
            </Space>
          </Space>
        </Header>

        {/* Main Content with Page Transitions */}
        <Content
          style={{
            margin: 24,
            padding: 0,
            minHeight: "calc(100vh - 64px - 48px)",
            background: "transparent",
            overflow: "auto",
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              style={{ height: "100%" }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </Content>

        {/* Branding Footer */}
        <div className="osg-branding-footer">
          Designed & Developed with <span style={{ color: colors.accent }}>♥</span> by{" "}
          <a href="https://techpotato.in" target="_blank" rel="noopener noreferrer">
            Techpotato Software LLP
          </a>
        </div>
      </Layout>
    </Layout>
  );
};

export default LayoutPage;
